import { StateContext, Store } from "@ngxs/store";
import { catchError, combineLatest, delayWhen, filter, map, of, take, tap } from "rxjs";
import { isNil, mergeWith } from 'lodash';

import * as API from '@hiptraveler/data-access/api';
import { ItineraryState, ItineraryStateModel } from "..";
import { getWindowRef, globalStateManagementStoreKey } from "@hiptraveler/common";

interface Props {
  ctx: StateContext<ItineraryStateModel>;
  data: API.RequestByIdDto;
  imageApi: API.ImageApiService;
  bookApi: API.BookApiService;
}

export function loadAndMergeItineraryMediaStreams({ ctx, data, imageApi, bookApi }: Props) {
  
  const store: Store = getWindowRef()[globalStateManagementStoreKey];

  const image$ = imageApi.getImageInformation(data).pipe(
    tap(response => API.validateResponse(response)),
    map(resp => resp.data?.content),
    catchError(() => of({}))
  );

  const yelp$ = imageApi.getYelpContent(data).pipe(
    tap(response => API.validateResponse(response)),
    map(resp => resp.data?.content),
    catchError(() => of({}))
  );

  const tripAdvisor$ = bookApi.getTripAdvisorData(data).pipe(
    tap(response => API.validateResponse(response)),
    map(resp => (resp.data?.error ? {} : resp.data)),
    catchError(() => of({}))
  );

  const imageContent$ = combineLatest([ image$, yelp$ ]).pipe(
    tap(([ imgContent, yelpContent ]) => {
      ctx.patchState({
        imageInformation: combineObject<API.ImageResultData>(
          imgContent,
          yelpContent
        )
      });
    })
  );

  const withTripAdvisor$ = tripAdvisor$.pipe(
    delayWhen(() => {
      const current = ctx.getState().imageInformation;
      return current
        ? of(null)
        : store.select(ItineraryState.imageInformation).pipe(
            filter(Boolean), take(1)
          );
    }),
    filter(Boolean),
    tap(taDataMap => {
      const current = ctx.getState().imageInformation!;
      ctx.patchState({
        imageInformation: { ...current, taDataMap }
      });
    })
  );

  return combineLatest([ imageContent$, withTripAdvisor$ ]);
}

function combineObject<T>(objectA: any, objectB: any): T {
  objectA = objectA || {};
  objectB = objectB || {};
  return mergeWith(
    {},
    objectA,
    objectB,
    (objValue: any, srcValue: any) => 
      isNil(objValue)
        ? srcValue
          : isNil(srcValue)
        ? objValue
          : objValue
  ) || {};
}
